import { useState } from "react";
import { UserPlus, Search, Check, X, Users, Send, Copy, Link, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface FollowingUser {
  id: string;
  displayName: string | null;
  username: string | null;
  profilePhotoUrl: string | null;
}

interface TagFriendsPopoverProps {
  following: FollowingUser[];
  selected: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  buttonSize?: "sm" | "default";
  "data-testid"?: string;
}

interface SelectedFriendChipsProps {
  following: FollowingUser[];
  selected: string[];
  onRemove: (id: string) => void;
}

function getInitials(displayName: string | null, username: string | null): string {
  const name = displayName || username || "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function InviteTab() {
  const [contact, setContact] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");
  const [statusMsg, setStatusMsg] = useState("");

  const handleInvite = async () => {
    const value = contact.trim();
    if (!value) return;
    setStatus("loading");
    try {
      const token = localStorage.getItem("auth_token") || localStorage.getItem("token");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const codeRes = await fetch("/api/referrals/code", { headers });
      const codeData = await codeRes.json();
      const code: string = codeData.code || "";
      const inviteUrl = `${window.location.origin}/?ref=${code}`;
      const inviteText = `Discover great food on Eatzy Kudoz! ${inviteUrl}`;

      const isEmail = value.includes("@");
      const method = isEmail ? "share_link" : "sms";

      const shared = await (async () => {
        if (navigator.share) {
          try {
            await navigator.share({ text: inviteText, url: inviteUrl });
            return true;
          } catch {
            return false;
          }
        }
        return false;
      })();

      if (!shared) {
        await navigator.clipboard.writeText(inviteUrl);
        setStatusMsg("Link copied!");
      } else {
        setStatusMsg("Shared!");
      }

      fetch("/api/referrals/invitations", {
        method: "POST",
        headers,
        body: JSON.stringify({
          method,
          recipientContact: value,
        }),
      }).catch(() => {});

      setStatus("done");
    } catch {
      setStatusMsg("Could not get invite link. Try again.");
      setStatus("done");
    }
  };

  if (status === "done") {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Check className="w-5 h-5 text-primary" />
        </div>
        <p className="text-sm font-medium">{statusMsg}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setStatus("idle"); setContact(""); setStatusMsg(""); }}
        >
          Invite someone else
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Enter their email or phone and share your personal invite link.
      </p>
      <div className="relative">
        <input
          type="text"
          placeholder="Email or phone number"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleInvite()}
          className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="input-invite-contact"
        />
      </div>
      <Button
        size="sm"
        className="w-full"
        onClick={handleInvite}
        disabled={!contact.trim() || status === "loading"}
      >
        {status === "loading" ? (
          <>
            <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
            Getting invite link…
          </>
        ) : (
          <>
            <Send className="w-3 h-3 mr-1.5" />
            Send Invite
          </>
        )}
      </Button>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-popover px-2 text-xs text-muted-foreground">or</span>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={async () => {
          try {
            const token = localStorage.getItem("auth_token") || localStorage.getItem("token");
            const headers: Record<string, string> = {};
            if (token) headers["Authorization"] = `Bearer ${token}`;
            const codeRes = await fetch("/api/referrals/code", { headers });
            const codeData = await codeRes.json();
            const code: string = codeData.code || "";
            const inviteUrl = `${window.location.origin}/?ref=${code}`;
            await navigator.clipboard.writeText(inviteUrl);
            setStatusMsg("Link copied!");
            setStatus("done");
          } catch {
            setStatusMsg("Could not copy link");
            setStatus("done");
          }
        }}
      >
        <Copy className="w-3 h-3 mr-1.5" />
        Copy invite link
      </Button>
    </div>
  );
}

/**
 * Renders removable chips for the currently selected/tagged friends.
 * Must be placed in a separate block below the trigger row.
 */
export function SelectedFriendChips({ following, selected, onRemove }: SelectedFriendChipsProps) {
  if (selected.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {selected.map((id) => {
        const friend = following.find((f) => f.id === id);
        if (!friend) return null;
        const displayName = friend.displayName || friend.username || "?";
        const initials = getInitials(friend.displayName, friend.username);
        return (
          <Badge
            key={id}
            variant="secondary"
            className="flex items-center gap-1 pr-1"
            data-testid={`tagged-friend-${id}`}
          >
            <Avatar className="w-4 h-4">
              <AvatarImage src={friend.profilePhotoUrl || undefined} alt={displayName} />
              <AvatarFallback className="text-[8px]">{initials}</AvatarFallback>
            </Avatar>
            <span className="text-xs">{displayName}</span>
            <button
              onClick={() => onRemove(id)}
              className="ml-0.5 rounded-full hover:text-destructive"
              data-testid={`remove-friend-${id}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        );
      })}
    </div>
  );
}

/**
 * Trigger button + popover only — no chip rendering.
 * Pair with <SelectedFriendChips> placed in a separate block below the trigger row.
 */
export default function TagFriendsPopover({
  following,
  selected,
  onAdd,
  onRemove,
  buttonSize = "sm",
  "data-testid": testId = "button-tag-friends",
}: TagFriendsPopoverProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"friends" | "invite">(
    following.length === 0 ? "invite" : "friends"
  );
  const [search, setSearch] = useState("");

  const filteredFriends = following.filter(
    (f) =>
      (f.displayName?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
      (f.username?.toLowerCase().includes(search.toLowerCase()) ?? false)
  );

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setTab(following.length === 0 ? "invite" : "friends");
      setSearch("");
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={buttonSize}
          data-testid={testId}
        >
          <UserPlus className="w-3 h-3 mr-1" />
          {selected.length > 0 ? `${selected.length} tagged` : "Add"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-76 p-0" align="end">
        {/* Tab headers */}
        <div className="flex border-b">
          <button
            onClick={() => setTab("friends")}
            className={cn(
              "flex-1 py-2 text-sm font-medium transition-colors",
              tab === "friends"
                ? "text-primary border-b-2 border-primary -mb-px"
                : "text-muted-foreground"
            )}
            data-testid="tab-friends"
          >
            <Users className="w-3.5 h-3.5 inline mr-1" />
            Friends
          </button>
          <button
            onClick={() => setTab("invite")}
            className={cn(
              "flex-1 py-2 text-sm font-medium transition-colors",
              tab === "invite"
                ? "text-primary border-b-2 border-primary -mb-px"
                : "text-muted-foreground"
            )}
            data-testid="tab-invite"
          >
            <Link className="w-3.5 h-3.5 inline mr-1" />
            Invite
          </button>
        </div>

        <div className="p-3">
          {tab === "friends" ? (
            <div className="space-y-2">
              {following.length > 0 && (
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search friends…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="input-search-friends"
                  />
                </div>
              )}

              {following.length === 0 ? (
                <div className="text-center py-4 space-y-2">
                  <Users className="w-8 h-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    You're not following anyone yet.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTab("invite")}
                  >
                    Invite friends instead
                  </Button>
                </div>
              ) : filteredFriends.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No friends found
                </p>
              ) : (
                <div className="max-h-52 overflow-y-auto space-y-0.5 -mx-1">
                  {filteredFriends.map((friend) => {
                    const isSelected = selected.includes(friend.id);
                    const displayName = friend.displayName || friend.username || "?";
                    const initials = getInitials(friend.displayName, friend.username);
                    return (
                      <button
                        key={friend.id}
                        onClick={() => (isSelected ? onRemove(friend.id) : onAdd(friend.id))}
                        className={cn(
                          "w-full flex items-center gap-3 px-2 py-2 rounded-md text-left overflow-visible hover-elevate active-elevate-2",
                          isSelected && "toggle-elevate toggle-elevated"
                        )}
                        data-testid={`friend-option-${friend.id}`}
                      >
                        <Avatar className="w-8 h-8 flex-shrink-0">
                          <AvatarImage src={friend.profilePhotoUrl || undefined} alt={displayName} />
                          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{displayName}</p>
                          {friend.username && friend.displayName && (
                            <p className="text-xs text-muted-foreground truncate">
                              @{friend.username}
                            </p>
                          )}
                        </div>
                        {isSelected && (
                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <InviteTab />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
